import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useAppSelector } from '@redux/hooks';
import { Icon, type IconName } from '@tkww/orion-web-ds-graphic';
import { GridColumn } from '@tkww/orion-web-ds-structure';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { TextBody } from '@tkww/orion-web-ds-text';
import classNames from 'classnames';
import difference from 'lodash/difference';
import map from 'lodash/map';
import React from 'react';
import type { VenueAmenity } from 'types/settings';
import type { Decorated } from 'types/vendor';
import VENUE_AMENITIES from '../../constants/venue_amenities';
import DetailsHelper from '../../helpers';
import { DetailsEmptyState } from '../DetailsEmptyState';
import Styles from './styles.scss';

interface AmenitiesContainerProps {
	amenities: string[];
	icon: IconName;
	showIcons?: boolean;
	shouldShowEmptyState?: boolean;
	breadcrumbUrl?: string;
}

const AmenitiesContainer = ({
	amenities,
	icon,
	shouldShowEmptyState,
	breadcrumbUrl,
}: AmenitiesContainerProps) => {
	const isLiteStorefront = useLiteStorefront();

	if (!amenities.length && shouldShowEmptyState) {
		return (
			<GridColumn xs="12">
				<DetailsEmptyState breadcrumbUrl={breadcrumbUrl} section="amenities" />
			</GridColumn>
		);
	}

	return (
		<>
			{amenities.map((amenity) => (
				<GridColumn
					xs="6"
					md="6"
					key={amenity.toLowerCase()}
					className={Styles.amenitiesWrapper}
				>
					{isLiteStorefront && (
						<Icon
							name={icon}
							className={Styles[icon]}
							size="md"
							aria-label={
								icon === 'check' ? 'Available amenity' : 'Unavailable amenity'
							}
						/>
					)}
					<div data-testid={`test-${icon}`} className={Styles.amenity}>
						{amenity}
					</div>
				</GridColumn>
			))}
		</>
	);
};

const AvailableAmenities = ({
	shouldShowEmptyState,
}: Pick<AmenitiesProps, 'shouldShowEmptyState'>) => {
	const vendor = useAppSelector(
		(state) => state.vendor.vendor as unknown as Decorated,
	);
	const isLiteStorefront = useLiteStorefront();
	const amenityNames = map(vendor.amenities, 'name');
	return (
		<AmenitiesContainer
			amenities={amenityNames}
			icon="check"
			showIcons={!isLiteStorefront}
			shouldShowEmptyState={shouldShowEmptyState}
			breadcrumbUrl={vendor.breadcrumbInfo.cityUrl}
		/>
	);
};

const RemainingAmenities = ({
	venueAmenities,
}: Pick<AmenitiesProps, 'venueAmenities'>) => {
	const vendor = useAppSelector(
		(state) => state.vendor.vendor as unknown as Decorated,
	);
	const isLiteStorefront = useLiteStorefront();
	const amenityIds = map(vendor.amenities, 'id');
	const remainingAmenities = difference(VENUE_AMENITIES, amenityIds);
	const remainingAmenityNames = DetailsHelper.findMatchingFacets(
		venueAmenities,
		remainingAmenities,
	);

	if (!isLiteStorefront) {
		return null;
	}

	return (
		<AmenitiesContainer
			amenities={remainingAmenityNames}
			icon="close"
			showIcons={true}
		/>
	);
};

interface AmenitiesProps {
	vendor: Decorated;
	venueAmenities?: VenueAmenity[];
	shouldShowEmptyState: boolean;
}

export const Amenities: React.FC<AmenitiesProps> = ({
	vendor,
	venueAmenities = [],
	shouldShowEmptyState,
}) => {
	const isLiteStorefront = useLiteStorefront();

	if (vendor.categoryCode !== 'REC') {
		return null;
	}

	return (
		<>
			<GridRow
				className={classNames({
					[Styles.detailSection]: !isLiteStorefront,
				})}
			>
				{!isLiteStorefront && (
					<GridColumn xs="12" md="4">
						<TextBody size="md" bold className={Styles.title}>
							Amenities
						</TextBody>
					</GridColumn>
				)}
				<GridColumn
					xs="12"
					md={!isLiteStorefront ? '8' : '12'}
					className={Styles.amenities}
				>
					<GridRow>
						<AvailableAmenities shouldShowEmptyState={shouldShowEmptyState} />
						<RemainingAmenities venueAmenities={venueAmenities} />
					</GridRow>
				</GridColumn>
			</GridRow>
			{!isLiteStorefront && <div className={Styles.divider} />}
		</>
	);
};
